import type { CopilotStatefulToolCall, CopilotToolCallStatus } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';
import plur from 'plur';

function trimPunctuation(text?: string) {
  if (text == null || isEmptyString(text)) {
    return '';
  }

  return text.trim().replace(/\.+$/, '');
}

export function combinedStatus(toolCalls: CopilotStatefulToolCall[]): CopilotToolCallStatus {
  const statuses = toolCalls.map((toolCall) => toolCall.status);
  if (statuses.includes('executing')) {
    return 'executing';
  } else if (statuses.includes('error')) {
    return 'error';
  } else if (statuses.includes('success')) {
    return 'success';
  }

  return 'success';
}

export type ToolCallLabelParts = {
  /** The main descriptive text for the header */
  text: string;
  /** A secondary note like "(+2 completed)" or "(+2 more)" */
  suffix: string;
};

export function toolCallLabel(toolCalls: CopilotStatefulToolCall[]): ToolCallLabelParts {
  const toolCount = toolCalls.length;
  const failedCount = toolCalls.filter((tc) => tc.status === 'error').length;
  const successCount = toolCalls.filter((tc) => tc.status === 'success').length;
  const status = combinedStatus(toolCalls);

  if (status === 'error') {
    const text =
      failedCount === toolCount
        ? `${toolCount} ${plur('tool', toolCount)} failed`
        : `Called ${toolCount} ${plur('tool', toolCount)}, ${failedCount} failed`;
    return { text, suffix: '' };
  }

  if (status === 'executing') {
    // Prioritize showing the first currently-executing tool
    const executingTool = toolCalls.find((tc) => tc.status === 'executing');
    const executingText = trimPunctuation(executingTool?.summary?.executing);
    const executingCount = toolCalls.filter((tc) => tc.status === 'executing').length;

    if (executingText.length > 0) {
      const suffix =
        successCount > 0
          ? `+${successCount} ${plur('other', successCount)} completed`
          : executingCount > 1
          ? `+${executingCount - 1} more`
          : '';
      return { text: executingText + '…', suffix };
    }

    // No summary — use a generic label scoped to executing count
    const suffix = successCount > 0 ? `+${successCount} completed` : '';
    return { text: `Calling ${executingCount} ${plur('tool', executingCount)}`, suffix };
  }

  // All success — show the last tool's success summary
  const lastSuccessTool = toolCalls.findLast(
    (tc) => tc.status === 'success' && trimPunctuation(tc.summary?.success).length > 0,
  );
  const successText = trimPunctuation(lastSuccessTool?.summary?.success);

  if (successText.length > 0) {
    const suffix = toolCount > 1 ? `+${toolCount - 1} more` : '';
    return { text: successText, suffix };
  }

  return { text: `Called ${toolCount} ${plur('tool', toolCount)}`, suffix: '' };
}
